import { useCallback, useEffect, useState } from 'react';
import store from 'store2';
import { isBrowser } from 'utils/environment';

import { DEFAULT_CONFIG, ROOT_KEY } from './constants';
import type { StorageConfig, Store } from './types';

export function useLocalStorage<T>(config: StorageConfig<T>) {
  const {
    name: key,
    defaultValue,
    version = DEFAULT_CONFIG.version,
    expirationMillis = DEFAULT_CONFIG.expirationMillis,
  } = config;

  const [value, setValue] = useState<T>(() => defaultValue);
  const [isHydrated, setIsHydrated] = useState(false);

  /* SSR */
  useEffect(() => {
    if (isBrowser) {
      const stored = getStoredValue<T>(key, { expirationMillis, version });
      if (stored !== null) {
        setValue(stored);
      }
      setIsHydrated(true);
    }
  }, [key, expirationMillis, version, isHydrated]);

  const setStoredValue = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      if (!isBrowser) return;

      try {
        store.transact(ROOT_KEY, (data: Store<T>) => {
          const currentData = data || {};
          const currentPayload = currentData[key]?.payload ?? defaultValue;

          const actualNewValue =
            typeof newValue === 'function'
              ? (newValue as (prevValue: T) => T)(currentPayload)
              : newValue;

          currentData[key] = {
            version,
            timestamp: Date.now(),
            payload: actualNewValue,
          };

          setValue(actualNewValue);

          return currentData;
        });
      } catch (error) {
        console.error(`Error saving to localStorage with key "${key}":`, error);
      }
    },
    [key, version, defaultValue],
  );

  const handleRemove = useCallback(() => {
    removeStoredValue(key);
    setValue(defaultValue);
  }, [key, defaultValue]);

  /* Expose retreival function to allow multiple components to get latest value */
  const getValue = useCallback(
    () => getStoredValue<T>(key, { expirationMillis, version }),
    [key, expirationMillis, version],
  );

  return {
    value,
    getValue,
    setStoredValue,
    removeStoredValue: handleRemove,
    isHydrated,
    isLoading: !isHydrated,
  };
}

function getStoredValue<T>(
  key: string,
  options: { expirationMillis?: number; version?: number } = {},
): T | null {
  if (!isBrowser) return null;

  const { expirationMillis, version } = options;

  try {
    const rootData = store.get(ROOT_KEY) as Store<T>;

    if (!rootData || !rootData[key]) return null;

    const storedItem = rootData[key];

    if (version !== undefined && storedItem.version !== version) {
      console.warn(
        `LocalStorage item version mismatch for ${key}. Expected: ${version}, Found: ${storedItem.version}`,
      );
      removeStoredValue(key);
      return null;
    }

    if (expirationMillis) {
      const expiryTime = storedItem.timestamp + expirationMillis;

      if (Date.now() > expiryTime) {
        console.info(`LocalStorage item expired: ${key}`);
        removeStoredValue(key);
        return null;
      }
    }

    return storedItem.payload;
  } catch (error) {
    console.error(`Error reading from localStorage with key "${key}":`, error);
    return null;
  }
}

function removeStoredValue(key: string) {
  if (!isBrowser) return;

  try {
    store.transact(ROOT_KEY, (data: Store<any>) => {
      const currentData = data || {};
      const { [key]: discard, ...remainingData } = currentData;
      return remainingData;
    });
  } catch (error) {
    console.error(`Error removing from localStorage with key "${key}":`, error);
  }
}
